<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transactions"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransactionsData"
            :has-errors="item.issues && item.issues.length > 0"
          />
          Transactions
        </span>
      </template>
      <validation-observer ref="observer">
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Item amount invoiced"
              rules="required"
            >
              <b-form-group>
                <label>
                  Item amount invoiced <span class="text-danger">*</span>
                  <small class="text-muted">1408000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Total amount invoiced for this item'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  id="value"
                  v-model="item.transaction_data.item_charge"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(item), 'value')"
                  :class="issueClass(getUnifiedIssues(item), 'value')"
                  type="number"
                  step="0.01"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Valuation method"
              rules="required"
            >
              <b-form-group>
                <label>
                  Valuation method <span class="text-danger">*</span>
                  <small class="text-muted">1410000000</small>
                </label>
                <b-form-input
                  v-model="item.transaction_data.customs_valuation_method"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Valuation indicators"
            >
              <b-form-group>
                <label>
                  Valuation indicators
                  <small class="text-muted">1407000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Indicators for valuation adjustments'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.transaction_data.valuation_adjustment_addition"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group>
              <label>
                Statistical value
                <small class="text-muted">9906000000</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Statistical value of the goods'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <b-form-input
                v-model="item.transaction_data.statistical_value"
                :disabled="disabled"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Nature of transaction"
            >
              <b-form-group>
                <label>
                  Nature of transaction
                  <small class="text-muted">9905000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Nature of the transaction'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.transaction_data.transaction_nature"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasTransactionsData() {
      if (!this.item || !this.item.transaction_data) {
        return false
      }
      const {
        item_charge: itemCharge,
        customs_valuation_method: customsValuationMethod,
        valuation_adjustment_addition: valuationAdjustmentAddition,
        statistical_value: statisticalValue,
        transaction_nature: transactionNature,
      } = this.item.transaction_data
      return !!(itemCharge || customsValuationMethod || valuationAdjustmentAddition || statisticalValue || transactionNature)
    },
  },
  mounted() {
    // Initialize item_charge from item.value when component mounts
    if (this.item && this.item.value && this.item.transaction_data) {
      if (!this.item.transaction_data.item_charge) {
        this.$set(this.item.transaction_data, 'item_charge', this.item.value)
      }
    }
  },
  methods: {
    getUnifiedIssues(item) {
      if (item.issues && item.issues.length > 0) {
        return item.issues
      }
      return []
    },
    issueDescription(issues, field) {
      if (issues?.length) {
        const index = issues.findIndex(item => item.field === field)

        if (index !== -1) {
          return issues[index].message
        }

        return ''
      }
      return ''
    },
    issueClass(issues, field) {
      if (issues?.length) {
        const index = issues.findIndex(item => item.field === field)

        if (index !== -1) {
          return issues[index].type === 'warning' ? 'input-warning' : 'input-danger'
        }

        return ''
      }
      return ''
    },
    async validate() {
      return this.$refs.observer.validate()
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
